import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import UserProfilePicture from './UserProfilePicture';
import { BADGE_CONFIG, STREAK_BADGE_HIERARCHY } from '../../config/badgeConfig';
import { useProfile } from '../../context/ProfileContext';
import useSongActions from '../../hooks/useSongActions';
import subscribeIcon from '../../assets/subscribe_grey.png';
import subscribeWhite from '../../assets/subscribe_white.png';
import subscribedIcon from '../../assets/subscribe_colour.png';
import ProfileBio from './ProfileBio';
import CreateSelectionModal from './CreateSelectionModal'; // 🌟 IMPORT NEW MODAL

const ProfileHeader = ({ profile, isOwnProfile = false, onCreatePlaylistClick, onCreateClick }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useProfile();
  const { isSubscribed, toggleSubscription } = useSongActions({ artistId: profile?.id });
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSelectCreateType = useCallback((type) => {
    // 1. Close the selection modal immediately
    setIsCreateOpen(false);

    // 2. Defer the parent modal opening to the next animation frame
    // This allows Framer Motion / React to cleanly unmount the selection modal first
    requestAnimationFrame(() => {
      if (onCreateClick) {
        onCreateClick(type);
      }
    });
  }, [onCreateClick]);

  if (!profile) return null;

  const highestStreakBadgeKey = STREAK_BADGE_HIERARCHY.find(key => profile.badges?.[key] === true);
  const highestStreakBadge = highestStreakBadgeKey ? BADGE_CONFIG[highestStreakBadgeKey] : null;

  const getSubscribeIcon = () => {
    if (isSubscribed) return subscribedIcon; 
    return isDarkMode ? subscribeWhite : subscribeIcon; 
  };

  const title = profile.type === 'artist' 
    ? (profile.artistName || 'Artist Profile') 
    : (profile.displayName || 'User Profile');

  return (
    <div className="relative w-full min-h-72 transition-colors duration-500">
      {/* Name / Title */}
      <h1 className="text-2xl font-bold text-center pt-8 tracking-tighter dark:text-brand-white">
        {title}
      </h1>
      
      {/* Handle display link tracking */}
      {profile.handle && (
        <p className={`text-lg text-center mt-1 ${isDarkMode ? 'text-brand-pink' : 'text-slate-600'}`}>
          @{profile.handle}
        </p>
      )}

      {/* Container for Picture and action buttons */}
      <div className="relative flex flex-col items-center mt-4 w-full">
        <UserProfilePicture 
          photoURL={profile.photoURL} 
          sizeClass="w-24 h-24" 
          isPremium={profile.type === 'user' && profile.premiumSubscription?.status === 'active'}
        />

        {profile.type === 'user' && profile.premiumSubscription?.status === 'active' && (
          <span className="mt-3 px-3 py-1 bg-[#ff00cc] text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm animate-in fade-in zoom-in-95 duration-200">
            Premium
          </span>
        )}
        
        <div className="flex items-center justify-center mt-2 h-8">
          {highestStreakBadge && (
            <img 
              src={highestStreakBadge.icon} 
              alt={highestStreakBadge.name}
              title={highestStreakBadge.name}
              className="w-7 h-7"
            />
          )}
        </div>

        {/* City, State metadata locations */}
        {(profile.city || profile.state) && (
          <p className="text-sm mt-1 font-bold dark:text-brand-white/80 text-slate-900">
            {profile.city && profile.state ? `${profile.city}, ${profile.state}` : (profile.city || profile.state)}
          </p>
        )}

        <ProfileBio bio={profile.bio} className="mt-4" />
      </div>

      {/* ✅ UNIFIED ACTION BUTTON CONTAINER */}
      <div className="absolute top-36 right-4 flex flex-col items-center z-20">

        {/* 1. 🌟 REFACTORED: Show redesigned 'Create' button layout for own artist profile */}
        {isOwnProfile && profile.type === 'artist' && (
          <button 
            onClick={() => setIsCreateOpen(true)}
            className="flex flex-col items-center group focus:outline-none transition-transform active:scale-95"
          >
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-brand-gray/70 flex items-center justify-center mb-1 group-hover:bg-brand-pink/10">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 dark:text-brand-white group-hover:text-brand-pink">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <span className="text-[10px] font-black tracking-widest dark:text-brand-white/70 group-hover:text-brand-pink">Create</span>
          </button>
        )}

        {/* 2. Show 'Subscribe' for other artist profiles */}
        {!isOwnProfile && profile?.type === 'artist' && (
          <div className="flex flex-col items-center w-20">
            <button 
              onClick={() => toggleSubscription(profile.id, profile.artistName, 'profile_page')} 
              className="flex flex-col items-center justify-center transition-all active:scale-90 group"
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-white/10 flex items-center justify-center mb-1 border border-transparent dark:border-white/5 transition-colors group-hover:bg-slate-200 dark:group-hover:bg-white/20">
                <img 
                  src={getSubscribeIcon()} 
                  alt="Subscribe" 
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className={`text-[10px] font-black uppercase tracking-widest transition-colors ${
                isSubscribed ? 'text-[#ff00cc]' : 'text-slate-500 dark:text-white/70'
              }`}>
                {isSubscribed ? 'Subscribed' : 'Subscribe'}
              </span>
            </button>
          </div>
        )}

        {/* 3. Show 'Create' for own user profile */}
        {isOwnProfile && profile.type === 'user' && (
          <button 
            onClick={onCreatePlaylistClick} 
            className="flex flex-col items-center justify-center group active:scale-90 transition-all"
          >
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-brand-gray/70 flex items-center justify-center mb-1 group-hover:bg-brand-pink/10">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 dark:text-brand-white group-hover:text-brand-pink">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
            <span className="text-[10px] font-black tracking-widest dark:text-brand-white/70 group-hover:text-brand-pink">Create</span>
          </button>
        )}
      </div>

      {/* 🌟 MOUNT THE MULTI-CHOICE OVERLAY SELECTION MODAL */}
      <CreateSelectionModal 
        isOpen={isCreateOpen} 
        onClose={() => setIsCreateOpen(false)} 
        // Forward the click callback action handler down to the choices
        onSelectType={(type) => {
            if (onCreateClick) onCreateClick(type);
        }}
      />
    </div>
  );
};

export default ProfileHeader;